import { useState } from 'react';
import { Tag, Tooltip, Space, Popover, InputNumber, Button, Typography, message } from 'antd';
import { TrophyOutlined, StarOutlined, GiftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { promotionService } from '@/services/promotion.service';

const { Text } = Typography;

export interface LoyaltyRedeemResult {
  points: number;
  value: number;
}

interface Props {
  customerId: string;
  /** Callback when points are successfully redeemed. */
  onRedeem?: (result: LoyaltyRedeemResult) => void;
  /** Currently redeemed points (to show chip). */
  redeemedPoints?: number;
  redeemedValue?: number;
}

export function LoyaltyBadge({ customerId, onRedeem, redeemedPoints, redeemedValue }: Props) {
  const { data: balance } = useQuery({
    queryKey: ['loyalty-balance', customerId],
    queryFn: () => promotionService.getLoyaltyBalance(customerId),
    enabled: !!customerId,
    staleTime: 30_000,
  });

  const { data: program } = useQuery({
    queryKey: ['loyalty-program'],
    queryFn: () => promotionService.getLoyaltyProgram(),
    staleTime: 60_000,
  });

  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState<number>(0);

  if (!balance?.data) return null;

  const points = balance.data.balance ?? 0;
  const tier = balance.data.tier_name;
  // BE returns nested redemption object: { redemption: { rate, min_points } }
  const redemptionRate = program?.data?.redemption?.rate ?? program?.data?.redemption_rate ?? 100;
  const minRedeem = program?.data?.redemption?.min_points ?? program?.data?.min_points_redeem ?? 0;

  const estimatedValue = redeemPoints * redemptionRate;

  const handleRedeem = () => {
    if (!redeemPoints || redeemPoints <= 0) return;
    if (redeemPoints > points) {
      void message.warning('Số điểm vượt quá số dư');
      return;
    }
    if (minRedeem > 0 && redeemPoints < minRedeem) {
      void message.warning(`Tối thiểu ${minRedeem.toLocaleString('vi-VN')} điểm`);
      return;
    }
    // Record intent locally — actual redemption happens on BE during order creation
    const value = estimatedValue;
    void message.success(`Sẽ dùng ${redeemPoints.toLocaleString('vi-VN')} điểm = ${value.toLocaleString('vi-VN')}₫ khi thanh toán`);
    onRedeem?.({ points: redeemPoints, value });
    setRedeemOpen(false);
    setRedeemPoints(0);
  };

  const redeemContent = (
    <div style={{ width: 240, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Text strong>Dùng điểm tích lũy</Text>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
        <Text type="secondary">Số dư hiện tại:</Text>
        <Text strong>{points.toLocaleString('vi-VN')} điểm</Text>
      </div>
      {minRedeem > 0 && (
        <div style={{ fontSize: 11, color: '#999' }}>
          Tối thiểu: {minRedeem.toLocaleString('vi-VN')} điểm
        </div>
      )}
      <InputNumber
        style={{ width: '100%' }}
        min={0}
        max={points}
        value={redeemPoints}
        onChange={(v) => setRedeemPoints(v ?? 0)}
        placeholder="Nhập số điểm"
        addonAfter="điểm"
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
        <Text type="secondary">Quy đổi ({redemptionRate.toLocaleString('vi-VN')}₫/điểm):</Text>
        <Text strong style={{ color: '#a4193d' }}>{estimatedValue.toLocaleString('vi-VN')}₫</Text>
      </div>
      <Button
        type="primary"
        size="small"
        block
        disabled={!redeemPoints || redeemPoints <= 0 || redeemPoints > points}
        onClick={handleRedeem}
        style={{ background: '#a4193d', borderColor: '#a4193d' }}
      >
        Xác nhận dùng điểm
      </Button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
      <Space size={4}>
        <Tooltip title={`${points.toLocaleString('vi-VN')} điểm tích lũy`}>
          <Tag icon={<StarOutlined />} color="gold" style={{ margin: 0 }}>
            {points.toLocaleString('vi-VN')} điểm
          </Tag>
        </Tooltip>
        {tier && (
          <Tag icon={<TrophyOutlined />} color="purple" style={{ margin: 0 }}>
            {tier}
          </Tag>
        )}
        {points > 0 && (
          <Popover
            content={redeemContent}
            trigger="click"
            open={redeemOpen}
            onOpenChange={setRedeemOpen}
            placement="bottomLeft"
          >
            <Tag
              icon={<GiftOutlined />}
              color="red"
              style={{ margin: 0, cursor: 'pointer' }}
            >
              Dùng điểm
            </Tag>
          </Popover>
        )}
      </Space>
      {/* Chip showing redeemed points */}
      {redeemedPoints != null && redeemedPoints > 0 && (
        <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
          Đã dùng {redeemedPoints.toLocaleString('vi-VN')} điểm = {(redeemedValue ?? 0).toLocaleString('vi-VN')}₫
        </Tag>
      )}
    </div>
  );
}
